import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  ModalTrigger,
  PrimaryButton,
  Text,
} from "@certn/cascadia-react";
import { useContract } from "@certn/cascadia-react/miscellaneous/contract/hooks";
import { css } from "@emotion/css";
import { useContext, useMemo } from "react";
import { Trans } from "react-i18next";

import { CLICKWRAP_ACCESS_ID } from "./constants";
import { ApplicationContext } from "../ApplicationContext";
import { useApplicationFormContext } from "../ApplicationFormContext";

interface Props {
  linkName: string;
  contractId: string;
  modalHeader: string;
}

export function ConsentNoticeModal(props: Props) {
  const { linkName, modalHeader } = props;
  const { caseId } = useApplicationFormContext();
  const { customerName } = useContext(ApplicationContext);

  const { renderData, contractIDs } = useMemo(
    () => ({
      renderData: { company_name: customerName },
      contractIDs: [props.contractId],
    }),
    [customerName, props.contractId]
  );

  const {
    data: contractsData,
    getContractHTML,
    isError,
  } = useContract({
    accessID: CLICKWRAP_ACCESS_ID,
    contractIDs,
    signerID: caseId,
    renderData,
  });

  const contractHTML =
    Boolean(contractsData && !isError) && getContractHTML(props.contractId);

  return (
    <ModalTrigger
      trigger={
        <Link
          testID={`open-${linkName}-notice-modal-link`}
          linkStyle="monochrome"
        >
          {linkName}
        </Link>
      }
    >
      <Modal>
        <Dialog>
          {({ close }) => (
            <>
              <DialogHeader>{modalHeader}</DialogHeader>

              <DialogContent>
                <div
                  className={styles.content}
                  dangerouslySetInnerHTML={
                    isError
                      ? undefined
                      : {
                          __html: contractHTML,
                        }
                  }
                  aria-label="contract-content"
                  role="textbox"
                  tabIndex={0}
                >
                  {isError && (
                    <Text color="text-weak" testID="contract-fetch-error">
                      <Trans>Something went wrong</Trans>
                    </Text>
                  )}
                </div>
              </DialogContent>

              <DialogFooter
                leftContent={
                  <Link
                    testID="contract-download-link"
                    href={
                      contractsData?.contract_data[props.contractId]
                        ?.download_url
                    }
                    target="_blank"
                    textColor="text-weak"
                    textStyle="global/body-secondary"
                  >
                    <Trans>Download</Trans>
                  </Link>
                }
              >
                <PrimaryButton onPress={close}>
                  <Trans>Close</Trans>
                </PrimaryButton>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

const styles = {
  content: css({
    flex: 1,
    overflow: "auto",
    // text color for Clickwrap document
    "& *": {
      color: `var(--text-weak) !important`,
    },
    // override Clickwrap styles
    "& .ps-contract-scroll": {
      maxHeight: "unset !important",
      padding: "0 !important",
      border: "none !important",
    },
  }),
};
